import {ReactNode, useContext, useEffect, useMemo, useState} from "react";
import {
	iIndustrialJob,
	iMaterial,
	IndustrialLocation,
	IndustrialTasksContext
} from "./useIndustrialTasksContextProvider";
import {API} from "../API/api";

const INDUSTRIAL_JOBS_STORAGE_KEY = "industrial_jobs";

const loadJobsFromLocalStorage = (): iIndustrialJob[] => {
	const storedJobs = localStorage.getItem(INDUSTRIAL_JOBS_STORAGE_KEY);

	if (!storedJobs) {
		return [];
	}

	try {
		const parsedJobs = JSON.parse(storedJobs);

		if (!Array.isArray(parsedJobs)) {
			return [];
		}

		return parsedJobs.map((job: iIndustrialJob & { timestamp: string }) => ({
			...job,
			timestamp: new Date(job.timestamp),
		}));
	} catch {
		return [];
	}
};

const getJobEndTime = (job: iIndustrialJob) =>
	new Date(job.timestamp).getTime() + job.durationInMinutes * 60 * 1000;

const isJobInProgress = (job: iIndustrialJob, now: number) =>
	getJobEndTime(job) > now;

const isJobCompleted = (job: iIndustrialJob, now: number) =>
	getJobEndTime(job) <= now;

export const IndustrialTaskProvider = ({children}: { children: ReactNode }) => {

	const [locations, setLocations] = useState<IndustrialLocation[]>([]);
	const [materials, setMaterials] = useState<iMaterial[]>([]);
	const [jobs, setJobs] = useState<iIndustrialJob[]>(loadJobsFromLocalStorage);
	const [now, setNow] = useState(Date.now());

	const Actions = {
		createJob: (job: iIndustrialJob) => {
			setJobs(prev => [...prev, job]);
		},
		clearJobs: (id: string) => {
			setJobs(prev => prev.filter(job => job.id !== id));
		}
	}

	const fetch = {
		locations: () => {
			API.getIndustrialLocations()
				.then(response => {
					setLocations(response.data.data ?? []);
				})
		},
		materials: () => {
			API.getIndustrialMaterials()
				.then(response => {
					setMaterials(response.data
						.filter((material: iMaterial) => material.refinable)
						.sort((a: iMaterial, b: iMaterial) => a.name.localeCompare(b.name)) ?? []);
				})
		}
	};

	useEffect(() => {
		fetch.locations();
		fetch.materials();
	}, []);

	useEffect(() => {
		localStorage.setItem(INDUSTRIAL_JOBS_STORAGE_KEY, JSON.stringify(jobs));
	}, [jobs]);

	useEffect(() => {
		const timer = window.setInterval(() => {
			setNow(Date.now());
		}, 1000);

		return () => window.clearInterval(timer);
	}, []);

	const inProgress = useMemo(
		() =>
			jobs
				.filter(job => isJobInProgress(job, now))
				.sort((a, b) => getJobEndTime(a) - getJobEndTime(b)),
		[jobs, now]
	);

	const completedTasks = useMemo(
		() =>
			jobs
				.filter(job => isJobCompleted(job, now))
				.sort((a, b) => getJobEndTime(b) - getJobEndTime(a)),
		[jobs, now]
	);

	return <IndustrialTasksContext.Provider value={{
		locations: locations,
		materials: materials,
		jobs: jobs,
		inProgress: inProgress,
		completedTasks: completedTasks,

		Actions: {
			createJob: Actions.createJob,
			clearJobs: Actions.clearJobs
		}
	}}>
		{ children }
	</IndustrialTasksContext.Provider>
}

export default () => useContext(IndustrialTasksContext)